import { ListItemIcon, Tab, type SxProps, type Theme } from '@mui/material';
import { OverridableComponent, OverrideProps } from '@mui/material/OverridableComponent';
import { forwardRef, ReactNode, useContext, useEffect } from 'react';
import { NavigationItemContext } from './context';
import { SelectableListItemButton, type SelectableListItemButtonOwnProps } from './SelectableListItemButton';

export interface PlainNavigationItemOwnProps {
  orientation?: 'horizontal' | 'vertical';
  active?: boolean;
  disableInteractionStyles?: boolean;
  icon?: ReactNode;
  collapse?: ReactNode;
  defaultExpanded?: boolean;
  slotProps?: SelectableListItemButtonOwnProps['slotProps'];
  children?: ReactNode;
  sx?: SxProps<Theme>;
}

interface PlainNavigationItemTypeMap<AdditionalProps = object, RootComponent extends React.ElementType = 'a'> {
  props: AdditionalProps & PlainNavigationItemOwnProps;
  defaultComponent: RootComponent;
}

export type PlainNavigationItemProps<
  RootComponent extends React.ElementType = PlainNavigationItemTypeMap['defaultComponent'],
  AdditionalProps = object,
> = OverrideProps<PlainNavigationItemTypeMap<AdditionalProps, RootComponent>, RootComponent> & {
  component?: React.ElementType;
};

/**
 * Navigation item that is not connected to any router via an adapter
 *
 * Supports horizonal or vertical orientation - ListItemButton or Tab components from @mds/ui.
 */
export const PlainNavigationItem = forwardRef<HTMLAnchorElement, PlainNavigationItemProps>(
  function PlainNavigationItem(props, ref) {
    const {
      orientation: orientationProp,
      children,
      icon,
      active,
      disableInteractionStyles,
      collapse,
      defaultExpanded,
      fullWidth,
      indicator,
      selectionFollowsFocus,
      slotProps,
      tabIndex,
      textColor,
      value,
      ...rest
    } = props as PlainNavigationItemProps & {
      fullWidth?: boolean;
      indicator?: ReactNode;
      selectionFollowsFocus?: boolean;
      textColor?: 'inherit' | 'primary' | 'secondary';
      value?: unknown;
    };
    const context = useContext(NavigationItemContext);
    const orientation = orientationProp ?? context?.orientation ?? 'horizontal';
    const selectionManagedByTabs = context?.selectionManagedByTabs;

    useEffect(() => {
      if (orientation === 'horizontal' && selectionManagedByTabs) {
        context?.setSelectedValue?.(value, !!active);
      }
    }, [active, context, orientation, selectionManagedByTabs, value]);

    if (orientation === 'horizontal') {
      return (
        <Tab
          component="a"
          {...rest}
          fullWidth={fullWidth}
          indicator={indicator}
          selectionFollowsFocus={selectionFollowsFocus}
          selected={active}
          label={children}
          tabIndex={selectionManagedByTabs ? tabIndex : (tabIndex ?? 0)}
          textColor={textColor}
          value={value}
          ref={ref}
        />
      );
    }

    return (
      <SelectableListItemButton
        component="a"
        {...rest}
        disableInteractionStyles={disableInteractionStyles}
        selected={active}
        collapse={collapse}
        defaultExpanded={defaultExpanded ?? active}
        slotProps={slotProps}
        ref={ref}
      >
        {icon && <ListItemIcon>{icon}</ListItemIcon>}
        {children}
      </SelectableListItemButton>
    );
  },
) as OverridableComponent<PlainNavigationItemTypeMap>;
